import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { Code2, Plug, Puzzle } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import heroImg from "@/assets/integrations-hero.webp";
import syncImg from "@/assets/integrations-sync.webp";
import apiImg from "@/assets/integrations-api.webp";

const IMG: Record<string, string> = { hero: heroImg, sync: syncImg, api: apiImg };

export const Route = createFileRoute("/platform/integrations")({
  head: () => ({
    meta: [
      { title: "Integrations & API — banana.cards" },
      { name: "description", content: "Two-way sync with Google, Outlook, Slack, Notion, Sheets and 50+ apps. Plus a clean REST API and signed webhooks." },
      { property: "og:title", content: "Integrations & API — banana.cards" },
      { property: "og:description", content: "Two-way sync with Google, Outlook, Slack, Notion, Sheets and 50+ apps. Plus a clean REST API and signed webhooks." },
      { property: "og:image", content: IMG['hero'] },
    ],
    links: alternateLinkList("/platform/integrations", "en"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Integrations"
      title="Plays nicely with"
      italic="the rest of your stack."
      description="Native integrations with Google, Outlook, Slack, Notion, Sheets, Zapier, n8n and 50+ tools. Plus a REST API and webhooks for everything else."
      heroImage={IMG['hero']}
      heroImageAlt="Banana.cards integrations grid featuring Google Workspace, Microsoft 365, HubSpot, Salesforce, Slack and Zapier"
      metrics={[{ v: "50+", l: "native apps" }, { v: "REST", l: "API" }, { v: "Webhooks", l: "everywhere" }]}
      bullets={[
        { icon: Puzzle, title: "Native apps", desc: "Two-way sync with the tools your team already uses." },
        { icon: Plug, title: "Zapier & n8n", desc: "5,000+ apps reachable via Zapier and n8n connectors." },
        { icon: Code2, title: "API & webhooks", desc: "Build your own — full REST API and signed webhooks." }
      ]}
      deepDives={[
        { eyebrow: "Sync", title: "Two-way, conflict", italic: "free.", description: "Contacts, deals and calendars sync both ways with conflict resolution and a sync log per record.", imageAlt: "Two-way contact and calendar sync with per-record sync log and conflict resolution", image: IMG["sync"], reverse: false, points: ["Google Workspace & Microsoft 365", "HubSpot, Salesforce & Pipedrive", "Slack, Notion and Sheets", "Per-record sync log"] },
        { eyebrow: "Build", title: "An API your devs", italic: "will like.", description: "Clean REST endpoints, OpenAPI spec, generous rate limits and signed webhooks for every event.", imageAlt: "Banana developer API documentation with OpenAPI 3 spec, signed HMAC webhooks and sandbox environment", image: IMG["api"], reverse: true, points: ["OpenAPI 3 spec", "Rate limits per workspace", "Signed HMAC webhooks", "Sandbox environment"] }
      ]}
      howItWorks={[
        { title: "Pick an app", desc: "Search 50+ native integrations." },
        { title: "Connect securely", desc: "OAuth, scoped tokens, audit logged." },
        { title: "Map & sync", desc: "Field mapping with previews." },
        { title: "Build more via API", desc: "Webhooks and REST for anything else." }
      ]}
      capabilities={[
        "Google & Microsoft sync",
        "HubSpot, Salesforce, Pipedrive",
        "Slack, Notion, Sheets",
        "Zapier & n8n connectors",
        "REST API with OpenAPI",
        "Signed HMAC webhooks",
        "Per-workspace rate limits",
        "Sandbox environment"
      ]}
      faq={[
        { q: "Which tools integrate with Banana out of the box?", a: "Native integrations include Zapier (6,000+ apps), Salesforce, HubSpot and Zoho CRM, plus first-party sync for Google (Calendar, Contacts, Gmail), Microsoft (Outlook, Teams, Office 365), Apple (iCloud Calendar, Wallet), Zoom, Google Meet, Stripe, Paddle and Apple/Google Pay. For anything else, a REST API + webhooks gets you there in minutes." },
        { q: "Is there a native Zapier integration?", a: "Yes — search 'Banana Cards' in Zapier. Use triggers like 'New Contact', 'New Booking', 'Deal Won' or 'Card Tapped' to push data into 6,000+ other apps (Sheets, Notion, Mailchimp, Slack, Airtable, ClickUp, etc.) without writing code." },
        { q: "Does Banana sync two-way with Salesforce, HubSpot and Zoho?", a: "Yes — contacts and key objects sync both directions on a configurable interval, with field-level mapping and conflict resolution rules. New contacts captured in Banana appear in your CRM; updates in your CRM flow back to Banana." },
        { q: "Is there a REST API and webhooks for custom builds?", a: "Yes — a full REST API covers contacts, deals, tasks, bookings, contracts, cards and analytics, and HMAC-signed webhooks fire on every key event so your systems can react in real time. An official TypeScript SDK and a Postman collection are available." },
        { q: "Is the API rate-limited?", a: "Yes — generous defaults for every plan (sufficient for normal workspace use) with higher per-second and per-day caps on Pro and Enterprise. Hit a 429 and you'll see clear retry-after headers." },
        { q: "Are webhooks secure?", a: "Every webhook is signed with HMAC-SHA256 using a per-workspace secret, includes a replay-protection timestamp, and supports automatic retries with exponential backoff if your endpoint returns a non-2xx." }
      ]}
      outro={ { title: "Connect everything.", italic: "Build anything.", description: "Native integrations, an API your team will love, and webhooks for everything else." } }
    />
  );
}
